import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { CMSExploreCategory } from "../../../../services/exploreCategories";
import { Subcategory } from "../../../../services/subcategories";
import { resolveExploreCategoryRoute } from "../../../../utils/categoryRoutes";
import { resolveImageUrl } from "../../../../utils/imageFallback";

interface ExploreCategoriesTabProps {
  categories: CMSExploreCategory[];
  subcategories?: Subcategory[];
  onEdit: (category?: CMSExploreCategory) => void;
  onDelete: (id: string) => void;
}

const ExploreCategoriesTab: React.FC<ExploreCategoriesTabProps> = ({
  categories,
  subcategories = [],
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
        >
          {t("admin.contentManagement.exploreCategories")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => onEdit()}
          sx={{
            background: "linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)",
            boxShadow: "0 2px 8px rgba(156,39,176,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("admin.contentManagement.addExploreCategory")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
          gap: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        {categories.map((category) => {
          const route = resolveExploreCategoryRoute(category, subcategories);
          return (
          <Card key={category.id} sx={{ position: "relative", height: "100%" }}>
            <Box
              sx={{
                height: 150,
                backgroundImage: `url(${resolveImageUrl(category.image_url, '/placeholder-product.png')})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 100%)",
                },
              }}
            />
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "start",
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {category.title_en || category.title_key}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.card_key || category.title_key}
                  </Typography>
                  {route && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", fontFamily: "monospace", mt: 0.5 }}
                    >
                      {route}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label={
                    category.is_active
                      ? t("admin.contentManagement.active")
                      : t("admin.contentManagement.inactive")
                  }
                  color={category.is_active ? "success" : "default"}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelEn")}: {category.title_en || "-"}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelAr")}: {category.title_ar || "-"}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("admin.contentManagement.fields.labelFr")}: {category.title_fr || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <Chip
                  label={`${t("admin.contentManagement.fields.displayOrder")}: ${category.display_order}`}
                  size="small"
                  sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title={t("admin.contentManagement.edit")}>
                    <IconButton
                      size="small"
                      onClick={() => onEdit(category)}
                      sx={{ color: "#00d4ff" }}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("admin.contentManagement.delete")}>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(category.id!)}
                      sx={{ color: "#ff3366" }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default ExploreCategoriesTab;